
import { useCallback, useMemo } from 'react';
import { Poll } from '@/types/poll';
import { useCollectionRealtime } from '../realtime/useCollectionRealtime';
import { debugVoteFlow } from '@/utils/debug';

export const usePollVotesRealtime = (
  polls: Poll[],
  setPolls: React.Dispatch<React.SetStateAction<Poll[]>>,
  userVotes: Record<string, number>,
  setUserVotes: (votes: Record<string, number>) => void
) => {
  const recentVotes = new Set<string>();

  // Function to mark a poll as recently voted to prevent double counting
  const markRecentVote = useCallback((pollId: string) => {
    recentVotes.add(pollId);
    // Remove from recent votes after 3 seconds
    setTimeout(() => {
      recentVotes.delete(pollId);
    }, 3000);
  }, []);

  // Helper function to update poll options and recalculate percentages
  const updatePollOptions = useCallback((poll: Poll, optionId: number) => {
    const updatedOptions = poll.options.map(option => {
      if (option.id === optionId) {
        const newVotes = option.votes + 1;
        return {
          ...option,
          votes: newVotes
        };
      }
      return option;
    });

    const newTotalVotes = poll.total_votes + 1;

    // Recalculate percentages for all options
    const optionsWithPercentages = updatedOptions.map(option => ({
      ...option,
      percentage: newTotalVotes > 0 ? Math.round((option.votes / newTotalVotes) * 100) : 0
    }));

    return {
      updatedOptions: optionsWithPercentages,
      newTotalVotes
    };
  }, []);

  // Memoize valid poll IDs and enable status to prevent hook order changes
  const { validPollIds, shouldEnableRealtime, filters } = useMemo(() => {
    const validIds = polls.filter(p => p.id && p.id.trim() !== '').map(p => p.id);
    const shouldEnable = validIds.length > 0;
    
    // Create filters based on valid polls
    const realtimeFilters = shouldEnable && validIds.length === 1 
      ? { pollId: validIds[0] } 
      : {};
    
    return {
      validPollIds: validIds,
      shouldEnableRealtime: shouldEnable,
      filters: realtimeFilters
    };
  }, [polls]);
  
  // Always call useCollectionRealtime to maintain hook order
  const realtimeResult = useCollectionRealtime(
    'votes',
    filters,
    {
      onItemAdded: useCallback((payload: any) => {
        if (!shouldEnableRealtime) return;
        
        debugVoteFlow('REALTIME_UNIVERSAL_VOTE', { payload });
        
        const newVote = payload as {
          poll_id: string;
          option_id: number;
          user_id?: string;
          anonymous_id?: string;
        };

        // Skip if this is a recent vote we just made
        if (recentVotes.has(newVote.poll_id)) {
          debugVoteFlow('REALTIME_UNIVERSAL_SKIP_RECENT', { pollId: newVote.poll_id });
          return;
        }

        // Check if this vote is for any of our monitored polls
        const relevantPoll = polls.find(poll => poll.id === newVote.poll_id);
        if (!relevantPoll) {
          debugVoteFlow('REALTIME_UNIVERSAL_IRRELEVANT_POLL', { pollId: newVote.poll_id });
          return;
        }

        // Update polls with the new vote using functional state update
        setPolls(currentPolls => currentPolls.map(poll => {
          if (poll.id === newVote.poll_id) {
            const { updatedOptions, newTotalVotes } = updatePollOptions(poll, newVote.option_id);

            const updatedPoll = {
              ...poll,
              options: updatedOptions,
              total_votes: newTotalVotes,
              anonymous_votes: newVote.anonymous_id ? (poll.anonymous_votes || 0) + 1 : poll.anonymous_votes,
              questions: {
                ...poll.questions,
                options: updatedOptions,
                total_votes: newTotalVotes
              }
            };

            debugVoteFlow('REALTIME_UNIVERSAL_POLL_UPDATED', {
              pollId: poll.id,
              optionId: newVote.option_id,
              newVoteCount: updatedOptions.find(o => o.id === newVote.option_id)?.votes,
              newTotal: updatedPoll.total_votes,
              isAnonymous: !!newVote.anonymous_id
            });

            return updatedPoll;
          }
          return poll;
        }));
      }, [shouldEnableRealtime, polls, setPolls, updatePollOptions]),

      onError: useCallback((error: any) => {
        debugVoteFlow('REALTIME_UNIVERSAL_ERROR', { error });
        console.error('[usePollVotesRealtime] Real-time error:', error);
      }, [])
    },
    shouldEnableRealtime // Enable only when we have valid polls
  );

  debugVoteFlow('REALTIME_UNIVERSAL_STATUS', { 
    isConnected: realtimeResult.isConnected,
    connectionStatus: realtimeResult.connectionStatus,
    pollCount: polls.length,
    validPollIds: validPollIds.length,
    shouldEnableRealtime
  });

  return { 
    markRecentVote,
    isConnected: realtimeResult.isConnected,
    connectionStatus: realtimeResult.connectionStatus
  };
};
